/**
 * Client-side Web Push helpers: register the service worker, ask permission,
 * subscribe, and unsubscribe. Safe on unsupported browsers (returns false).
 */
import { API_URL, getToken } from './api';
import axios from 'axios';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const getNotificationPermission = () =>
  pushSupported() ? Notification.permission : 'unsupported';

export const registerServiceWorker = async () => {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/service-worker.js');
  } catch (e) {
    console.error('SW registration failed:', e);
    return null;
  }
};

/** Ask permission + subscribe this device. Returns true on success. */
export const enablePush = async () => {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: permission };

  const reg = await registerServiceWorker();
  if (!reg) return { ok: false, reason: 'no-sw' };
  await navigator.serviceWorker.ready;

  // Fetch the server's public VAPID key
  const { data } = await axios.get(`${API_URL}/api/notifications/vapid-key`);
  const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

  const existing = await reg.pushManager.getSubscription();
  const subscription = existing || (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  }));

  await axios.post(
    `${API_URL}/api/notifications/subscribe`,
    { subscription },
    { headers: { Authorization: `Bearer ${getToken()}` } }
  );
  return { ok: true };
};

/** Turn off notifications on this device. */
export const disablePush = async () => {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await axios.post(
      `${API_URL}/api/notifications/unsubscribe`,
      { endpoint: sub.endpoint },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    await sub.unsubscribe();
  }
};
